// MMO 공유 필드: 다른 플레이어 렌더(REBIRTH-MODES-SPEC 4.2, 레인 M).
// - 영웅 모델(카엘 T1 GLB)을 몹과 같은 GPU 스키닝(골격 텍스처, render/actors/skinning.ts)으로 구워 InstancedMesh 하나로 그린다.
//   인스턴스 속성 둘: aT = (x, y, z, yaw), aPose = (클립 A 프레임, 클립 B 프레임, B 비율, 영웅 0/1). 클립 바꿈은 0.15초 섞는다.
// - 이름표: 슬롯마다 미리 만든 캔버스 스프라이트(이름 + 회차 별). 칸에 새 사람이 들어올 때(serial이 바뀔 때)만 다시 그린다.
// - 위치·방향은 FieldNet.peers의 이전/다음 점을 보간한다(samplePeer, 지연 120ms).
// 프레임 루프(update)는 새 객체를 만들지 않는다. GLB를 못 읽으면 캡슐 그레이박스로 대신한다(게임은 계속된다).
import * as THREE from 'three/webgpu';
import * as TSL from 'three/tsl';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { HeroAct } from '../../shared/types';
import { FIELD_CHANNEL_CAPACITY, samplePeer, type FieldPeer, type PeerPose } from '../../shared/fieldNet';
import { bakeSkin, clipFrame, skinNodes, type SkinAtlas } from '../actors/skinning';
import { toonGradient } from '../actors/toon';
import { KAEL_SCALE, KAEL_URL } from '../kael';

// TSL 식 조립은 타입을 느슨하게 쓴다(render/actors/mobMotion.ts와 같다)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
const { attribute, cos, float, mix, normalize, normalGeometry, normalView, positionGeometry, positionViewDirection, sin, transformNormalToView, vec3 } = TSL as unknown as Record<string, N>;

/** 슬롯 수: 채널 정원 + 나(QA 페이지·필드 장면이 자기 영웅을 이 렌더로 그릴 때) */
export const PEER_SLOTS = FIELD_CHANNEL_CAPACITY + 1;
/** 구울 클립(카엘 GLB 안 이름). 순서가 클립 번호다 */
const CLIPS = ['idle', 'run', 'atk1', 'atk4', 'dodge', 'hurt', 'death', 'victory'] as const;
const C_IDLE = 0, C_RUN = 1, C_ATK = 2, C_SKILL = 3, C_DODGE = 4, C_HURT = 5, C_DEATH = 6, C_VICTORY = 7;
const LOOP = [true, true, true, true, false, false, false, true];
const BLEND_S = 0.15;
const LABEL_Y = 2.7;
/** 회차별 이름표 색(0 기본 금빛, 1 청금, 2+ 진홍 — 환생 외형 오라와 같은 계열) */
const CYCLE_COLOR = ['#F2C14E', '#6fb7ff', '#ff5a6a'];

/** HeroAct → 클립 번호(달리기는 실제 속도로 정한다) */
function clipOf(act: number, speed: number): number {
  switch (act) {
    case HeroAct.Attack: return C_ATK;
    case HeroAct.Skill: case HeroAct.Awaken: return C_SKILL;
    case HeroAct.Dodge: return C_DODGE;
    case HeroAct.Hurt: return C_HURT;
    case HeroAct.Dead: return C_DEATH;
    case HeroAct.Victory: return C_VICTORY;
    default: return speed > 0.6 ? C_RUN : C_IDLE;
  }
}

/** 한 사람 그리기 입력(나 자신을 그릴 때도 같은 모양) */
export interface PeerDraw { x: number; z: number; facing: number; speed: number; act: number }

interface Label { sprite: THREE.Sprite; canvas: HTMLCanvasElement; tex: THREE.CanvasTexture; serial: number }

export class PeersVisual {
  readonly group = new THREE.Group();
  /** 이번 프레임에 그린 사람 수(QA) */
  drawn = 0;
  /** 모델이 준비됐는가(false면 그레이박스) */
  skinned = false;
  private mesh: THREE.InstancedMesh | null = null;
  private skin: SkinAtlas | null = null;
  private readonly aT = new THREE.InstancedBufferAttribute(new Float32Array(PEER_SLOTS * 4), 4);
  private readonly aPose = new THREE.InstancedBufferAttribute(new Float32Array(PEER_SLOTS * 4), 4);
  private readonly labels: Label[] = [];
  // 슬롯별 애니메이션 상태(초)
  private readonly clip = new Int8Array(PEER_SLOTS).fill(-1);
  private readonly clipAt = new Float64Array(PEER_SLOTS);
  private readonly prevClip = new Int8Array(PEER_SLOTS);
  private readonly prevAt = new Float64Array(PEER_SLOTS);
  private readonly blendAt = new Float64Array(PEER_SLOTS);
  private readonly runPhase = new Float64Array(PEER_SLOTS);
  private readonly pose: PeerPose = { x: 0, z: 0, facing: 0, speed: 0 };
  private flip = 0;
  private runSpeed = 4;
  private scale = KAEL_SCALE;
  private lastMs = 0;
  /** 나 자신(선택): setSelf로 채우면 마지막 슬롯에 그린다 */
  private self: PeerDraw | null = null;
  private selfName = '';
  private selfCycle = 0;
  private selfHero = 0;
  private selfSerial = 0;

  constructor() {
    for (const a of [this.aT, this.aPose]) a.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < PEER_SLOTS; i++) {
      const canvas = document.createElement('canvas');
      canvas.width = 256; canvas.height = 64;
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false, fog: false }));
      // 게임 카메라(필드)는 QA 페이지보다 가깝다 — 3.6m 띠는 영웅보다 커 보였다(E2E 2026-10-28). 머리 위 작은 이름표로
      sprite.scale.set(1.9, 0.475, 1);
      sprite.renderOrder = 20;
      sprite.visible = false;
      this.group.add(sprite);
      this.labels.push({ sprite, canvas, tex, serial: -1 });
    }
  }

  /** 모델을 읽어 인스턴스 메시를 만든다. 실패해도 reject하지 않고 그레이박스로 대신한다 */
  async load(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(KAEL_URL);
      let src: THREE.SkinnedMesh | null = null;
      gltf.scene.traverse((o) => { if (!src && (o as THREE.SkinnedMesh).isSkinnedMesh) src = o as THREE.SkinnedMesh; });
      if (!src) throw new Error('카엘 GLB에 SkinnedMesh가 없다');
      const mesh = src as THREE.SkinnedMesh;
      const clips = CLIPS.map((n) => gltf.animations.find((c) => c.name === n));
      if (clips.some((c) => !c)) throw new Error('필수 클립 없음');
      // 정면 확인(KaelModel과 같은 규칙): 대기 자세에서 발가락이 -Z면 반 바퀴
      const mixer = new THREE.AnimationMixer(gltf.scene);
      mixer.clipAction(clips[C_IDLE]!).play(); mixer.update(0);
      gltf.scene.updateMatrixWorld(true);
      const toe = mesh.skeleton.bones.find((b) => /LeftToeBase$/.test(b.name)), foot = mesh.skeleton.bones.find((b) => /LeftFoot$/.test(b.name));
      if (toe && foot) this.flip = toe.getWorldPosition(new THREE.Vector3()).z - foot.getWorldPosition(new THREE.Vector3()).z < 0 ? Math.PI : 0;
      mixer.stopAllAction(); mixer.uncacheRoot(gltf.scene);
      // 정규화 정수(양자화) 속성은 실수로 펼친 뒤 월드 행렬을 적용한다(render/actors/gltf.ts prepare와 같다)
      gltf.scene.updateMatrixWorld(true);
      const geometry = new THREE.BufferGeometry();
      for (const key of ['position', 'normal'] as const) {
        const s = mesh.geometry.getAttribute(key);
        const v = new Float32Array(s.count * 3);
        for (let i = 0; i < s.count; i++) { v[i * 3] = s.getX(i); v[i * 3 + 1] = s.getY(i); v[i * 3 + 2] = s.getZ(i); }
        geometry.setAttribute(key, new THREE.BufferAttribute(v, 3));
      }
      for (const key of ['uv', 'skinIndex', 'skinWeight'] as const) {
        const s = mesh.geometry.getAttribute(key);
        if (s) geometry.setAttribute(key, s as THREE.BufferAttribute);
      }
      if (mesh.geometry.index) geometry.setIndex(mesh.geometry.index);
      geometry.applyMatrix4(mesh.matrixWorld);
      const skin = bakeSkin(gltf.scene, mesh, clips as THREE.AnimationClip[]);
      this.skin = skin;
      this.runSpeed = Math.max(1, (skin.clips.run?.speed ?? 2.7) * this.scale);
      const map = (mesh.material as THREE.MeshStandardMaterial).map ?? null;
      this.build(geometry, map, skin);
      this.skinned = true;
    } catch (err) {
      console.warn('다른 플레이어 모델을 읽지 못해 그레이박스로 대신한다', err);
      const g = new THREE.CapsuleGeometry(0.35, 1.0, 4, 10);
      g.translate(0, 0.85, 0);
      this.scale = 1;
      this.build(g, null, null);
    }
  }

  private build(geometry: THREE.BufferGeometry, map: THREE.Texture | null, skin: SkinAtlas | null): void {
    geometry.setAttribute('aT', this.aT);
    geometry.setAttribute('aPose', this.aPose);
    const aT: N = attribute('aT', 'vec4'), aP: N = attribute('aPose', 'vec4');
    const sk = skin ? skinNodes(skin, aP.xyz, positionGeometry, normalGeometry, normalGeometry) : { position: positionGeometry, normal: normalGeometry };
    const yaw = aT.w.add(this.flip), cy = cos(yaw), sy = sin(yaw);
    const turn = (v: N): N => vec3(cy.mul(v.x).add(sy.mul(v.z)), v.y, cy.mul(v.z).sub(sy.mul(v.x)));
    const mat = new THREE.MeshToonNodeMaterial({ gradientMap: toonGradient(), map: map ?? undefined, color: map ? 0xffffff : 0x2c3a66 });
    mat.positionNode = turn(sk.position).mul(this.scale).add(aT.xyz);
    const nw = normalize(turn(sk.normal));
    mat.normalNode = normalize(transformNormalToView(nw));
    // 림: 카엘 진홍 / 라이라 청록(영웅 표시)
    const fres = float(1).sub(normalView.dot(positionViewDirection).clamp(0, 1)).pow(3);
    (mat as unknown as { emissiveNode: N }).emissiveNode = mix(vec3(1.0, 0.16, 0.24), vec3(0.3, 0.85, 0.95), aP.w).mul(fres.mul(0.55));
    const m = new THREE.InstancedMesh(geometry, mat, PEER_SLOTS);
    m.frustumCulled = false;
    m.castShadow = true;
    m.receiveShadow = true;
    m.count = 0;
    const id = new THREE.Matrix4();
    for (let i = 0; i < PEER_SLOTS; i++) m.setMatrixAt(i, id);
    this.mesh = m;
    this.group.add(m);
  }

  /** 나 자신을 같은 렌더로 그린다(QA 페이지). null이면 그리지 않는다 */
  setSelf(d: PeerDraw | null, name = '', cycle = 0, hero = 0): void {
    this.self = d;
    if (name !== this.selfName || cycle !== this.selfCycle || hero !== this.selfHero) { this.selfName = name; this.selfCycle = cycle; this.selfHero = hero; this.selfSerial++; }
  }

  /** 매 프레임: peers(보간)와 나를 인스턴스 속성에 쓴다. nowMs = performance.now(FieldNet과 같은 시계) */
  update(peers: readonly FieldPeer[], nowMs: number): void {
    const dt = this.lastMs ? Math.min(0.1, (nowMs - this.lastMs) / 1000) : 0;
    this.lastMs = nowMs;
    const t = nowMs / 1000;
    let n = 0;
    for (let i = 0; i < peers.length && i < FIELD_CHANNEL_CAPACITY; i++) {
      const p = peers[i];
      const label = this.labels[i];
      if (!p.active || !p.visible) { label.sprite.visible = false; continue; }
      const s = samplePeer(p, nowMs, this.pose);
      this.writeSlot(n, i, s.x, s.z, s.facing, s.speed, p.act, p.hero, t, dt);
      this.placeLabel(label, p.serial, p.name, p.cycle, s.x, s.z);
      n++;
    }
    const selfLabel = this.labels[PEER_SLOTS - 1];
    if (this.self) {
      const d = this.self;
      this.writeSlot(n, PEER_SLOTS - 1, d.x, d.z, d.facing, d.speed, d.act, this.selfHero, t, dt);
      this.placeLabel(selfLabel, this.selfSerial, this.selfName, this.selfCycle, d.x, d.z);
      n++;
    } else selfLabel.sprite.visible = false;
    this.drawn = n;
    const m = this.mesh;
    if (!m) return;
    m.count = n;
    m.visible = n > 0;
    this.aT.needsUpdate = true;
    this.aPose.needsUpdate = true;
  }

  /** 인스턴스 n에 슬롯 s(애니메이션 상태 번호)의 자세를 쓴다 */
  private writeSlot(n: number, s: number, x: number, z: number, facing: number, speed: number, act: number, hero: number, t: number, dt: number): void {
    const c = clipOf(act, speed);
    if (this.clip[s] !== c) {
      if (this.clip[s] >= 0) { this.prevClip[s] = this.clip[s]; this.prevAt[s] = this.clipAt[s]; this.blendAt[s] = t; }
      else { this.prevClip[s] = c; this.prevAt[s] = t; this.blendAt[s] = t - BLEND_S; }
      this.clip[s] = c; this.clipAt[s] = t;
      if (c === C_RUN) this.runPhase[s] = 0;
    }
    const tv = this.aT.array as Float32Array, pv = this.aPose.array as Float32Array, o = n * 4;
    tv[o] = x; tv[o + 1] = 0; tv[o + 2] = z; tv[o + 3] = facing;
    const k = Math.min(1, (t - this.blendAt[s]) / BLEND_S);
    const skin = this.skin;
    if (skin) {
      // 달리기는 보간 속도에 맞춰 재생 속도를 바꾼다(발 미끄러짐 줄이기)
      if (c === C_RUN) this.runPhase[s] += dt * Math.min(1.6, Math.max(0.6, speed / this.runSpeed));
      const cur = c === C_RUN ? this.runPhase[s] : t - this.clipAt[s];
      const pc = this.prevClip[s];
      const prev = pc === C_RUN ? this.runPhase[s] : t - this.prevAt[s];
      pv[o] = clipFrame(skin, CLIPS[pc], prev, LOOP[pc]);
      pv[o + 1] = clipFrame(skin, CLIPS[c], cur, LOOP[c]);
      pv[o + 2] = k;
    } else { pv[o] = 0; pv[o + 1] = 0; pv[o + 2] = 0; }
    pv[o + 3] = hero ? 1 : 0;
  }

  private placeLabel(l: Label, serial: number, name: string, cycle: number, x: number, z: number): void {
    if (l.serial !== serial) { l.serial = serial; drawLabel(l, name, cycle); }
    l.sprite.position.set(x, LABEL_Y, z);
    l.sprite.visible = true;
  }

  dispose(): void {
    if (this.mesh) { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
    this.skin?.texture.dispose();
    for (const l of this.labels) { l.tex.dispose(); l.sprite.material.dispose(); }
  }
}

/** 이름표: 남색 판 + 회차 색 테두리 + 이름 + 회차 별(★ × 회차) */
function drawLabel(l: Label, name: string, cycle: number): void {
  const g = l.canvas.getContext('2d');
  if (!g) return;
  const accent = CYCLE_COLOR[Math.min(cycle, CYCLE_COLOR.length - 1)];
  g.clearRect(0, 0, 256, 64);
  g.fillStyle = 'rgba(16,22,42,0.82)';
  g.strokeStyle = accent;
  g.lineWidth = 3;
  g.beginPath();
  g.roundRect(4, 8, 248, 48, 14);
  g.fill(); g.stroke();
  const stars = cycle > 0 ? '★'.repeat(Math.min(cycle, 5)) + ' ' : '';
  g.font = '800 ' + (name.length > 8 ? 22 : 26) + 'px system-ui, "Malgun Gothic", sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = accent;
  g.fillText(stars + name, 128, 33);
  l.tex.needsUpdate = true;
}
